import {
  CodeStatus,
  HarbourOffice,
  HotWorkSection,
  OrderedBy,
  PermitCategory,
  PermitType,
  RadioButtonOption,
  SignatureType,
} from "../../../common/enums";
import { formElement } from "../../forms/FormElement";
import EnergyHarbourPermitFormState from "../../forms/Permits/EnergyHarbourPermit/EnergyHarbourPermitFormState";
import Person, { Person_default, Person_toFormElement } from "../../Person";
import Position, { Position_toPositionPickerRow } from "../../Position";
import Signature, { Signature_normalize } from "../../Signature";
import Measures, {
  Measures_normalize,
  Measures_toFormState,
} from "../conditions/Measures";
import ProtectiveEquipment, {
  ProtectiveEquipment_toFormState,
} from "../conditions/ProtectiveEquipment";
import {
  RiskIdentifications,
  RiskIdentifications_toFormState,
} from "../conditions/RiskIdentifications";
import {
  HotWorkConditions as HotWorkConditions,
  HotWorkConditions_normalize,
  HotWork_toFormState,
} from "./HotWorkConditions";
import HotWorkCategoryOneOptions, {
  HotWorkCategoryOneOptions_toFormState,
} from "./HotWorkCategoryOneOptions";
import HotWorkCategoryThreeOptions, {
  HotWorkCategoryThreeOptions_toFormState,
} from "./HotWorkCategoryThreeOptions";
import HotWorkCategoryTwoOptions, {
  HOT_WORK_CATEGORY_TWO_OPTIONS_VERSION_2026_03,
  HotWorkCategoryTwoOptions_toFormState,
} from "./HotWorkCategoryTwoOptions";
import PermitAgreement, {
  PermitAgreement_toFormState,
} from "./PermitAgreement";
import SafetyAgreement from "./SafetyAgreement";
import { permitContentVersions } from "../../WorkPermit";

type EnergyHarbourPermit = {
  id: string;
  status: CodeStatus;
  sequenceIdentifier: string;
  version: number;
  hasAttachedFile: boolean;
  isFinished: boolean;
  entrepreneur: string;
  description: string;
  departmentId: number;
  permitName: PermitType.EnergyHarbourPermit;
  permitCategories: PermitCategory[];
  startDate: Date;
  endDate: Date;
  entityId: number;
  persons: Person[];
  positions: Position[];
  remindMe: boolean;
  expirationReminderEmail: string | undefined;
  expirationReminderPhoneNumber: string | undefined;
  remindMeDate: Date;

  harbourOffice: HarbourOffice;
  educationCheckPerformed: boolean;
  measures: Measures;
  protectiveEquipment: ProtectiveEquipment;
  riskIdentifications: RiskIdentifications;

  supportsCategorySpecificHotWorkConditions: boolean;
  supportsPositionCategories: boolean;
  supportsMultiplePermitCategories: boolean;

  hotWorkInternalDirectorFlammableProductSignature: Signature;
  hotWorkExternalDirectorFlammableProductSignature: Signature;

  hotWork: HotWorkConditions;

  hotWorkCategoryOneConditions: HotWorkConditions;
  hotWorkCategoryTwoConditions: HotWorkConditions;
  hotWorkCategoryThreeConditions: HotWorkConditions;

  permitAgreement: PermitAgreement;
  safetyAgreement: SafetyAgreement;
  hotWorkCategoryOneOptions: HotWorkCategoryOneOptions;
  hotWorkCategoryTwoOptions: HotWorkCategoryTwoOptions;
  hotWorkCategoryThreeOptions: HotWorkCategoryThreeOptions;
  workLeader: Person;
  projectLeader: Person;
  allInvolvedHaveSafeHarbourEducation: boolean;
  canAttestPermitDone: boolean;
  canAttestPermitTidyed: boolean;
  PermitSeekerSignatureDate: Date;
  permitProviderSignatureDate: Date;
  orderedBy: OrderedBy;
  orderedByCompanyId: string | undefined;
  orderedByCompanyDepartmentId: number | undefined;
  orderedByCustomerNameSnapshot: string | undefined;

  renewalOfPermitSequenceIdentifier: string;
  renewedByPermitSequenceIdentifier: string;
  initialStartDate: Date;
  extendedEndDate: Date;

  lastPermitInSeriesDocumentId: string;

  safeHarbourEducationControlledSignature: Signature;
  isFinishedPermitSeekerSignature: Signature;
  isFinishedPermitProviderSignature: Signature;

  alertInfo: string[];
  showMissingHotWorkSignatureAlert: boolean;

  contentVersion: number;

  canUpdate: boolean;
  canAccept: boolean;
  canCheckout: boolean;
  canPrint: boolean;
  canCopy: boolean;
  canRenew: boolean;
  canManageAttachments: boolean;
  canUpdatePositionActiveStatus: boolean;
  canManageOrderedBy: boolean;
  canUpdateParticipants: boolean;
  canViewFinishPermitLink: boolean;
  canManageHotWorkConditions: boolean;
  canUpdatePermitAgreementEndDate: boolean;
  canUpdateDescription: boolean;
  canUpdateStartDate: boolean;
  canUpdateEndDate: boolean;
  canUpdatePositions: boolean;
  canManagePermitCategory: boolean;
  canManagePositionPolygons: boolean;
  canManagePositionCategories: boolean;

  isColdWorkCategoryRequired: boolean;

  createdByUserName: string;
};

export default EnergyHarbourPermit;

export function EnergyHarbourPermit_normalize(
  permit: EnergyHarbourPermit,
): EnergyHarbourPermit {
  const normalizedPermit: EnergyHarbourPermit = {
    positions: permit.positions.sort((a, b) => a.order - b.order),
    remindMeDate: new Date(permit.remindMeDate),
    startDate: new Date(permit.startDate),
    endDate: new Date(permit.endDate),
    safetyAgreement: {
      safetyAgreementPermitSeekerSignature: Signature_normalize(
        permit.safetyAgreement.safetyAgreementPermitSeekerSignature,
        SignatureType.SafetyAgreementPermitSeeker,
      ),
    },
    permitAgreement: {
      startDate: permit.permitAgreement.startDate
        ? new Date(permit.permitAgreement.startDate)
        : new Date(),
      endDate: permit.permitAgreement.endDate
        ? new Date(permit.permitAgreement.endDate)
        : new Date(),
      permitAgreementProviderSignature: Signature_normalize(
        permit.permitAgreement.permitAgreementProviderSignature,
        SignatureType.PermitAgreementPermitProvider,
      ),
      permitAgreementSeekerSignature: Signature_normalize(
        permit.permitAgreement.permitAgreementSeekerSignature,
        SignatureType.PermitAgreementPermitSeeker,
      ),
      fireGuard: permit.permitAgreement.fireGuard ?? "",
      isColdWorkOnly: permit.permitAgreement.isColdWorkOnly ?? false,
      wayOfCommunication: permit.permitAgreement.wayOfCommunication ?? "",
    },
    supportsMultiplePermitCategories:
      permit.supportsMultiplePermitCategories ?? false,
    supportsCategorySpecificHotWorkConditions:
      permit.supportsCategorySpecificHotWorkConditions ?? false,
    supportsPositionCategories: permit.supportsPositionCategories ?? false,
    hotWorkInternalDirectorFlammableProductSignature: Signature_normalize(
      permit.hotWorkInternalDirectorFlammableProductSignature,
      SignatureType.HotWorkInternalDirectorFlammableProduct,
    ),
    hotWorkExternalDirectorFlammableProductSignature: Signature_normalize(
      permit.hotWorkExternalDirectorFlammableProductSignature,
      SignatureType.HotWorkExternalDirectorFlammableProduct,
    ),
    hotWork: HotWorkConditions_normalize(permit.hotWork),
    hotWorkCategoryOneConditions: HotWorkConditions_normalize(
      permit.hotWorkCategoryOneConditions,
    ),
    hotWorkCategoryTwoConditions: HotWorkConditions_normalize(
      permit.hotWorkCategoryTwoConditions,
    ),
    hotWorkCategoryThreeConditions: HotWorkConditions_normalize(
      permit.hotWorkCategoryThreeConditions,
    ),
    riskIdentifications: {
      heavyLiftingChecklist: {
        craneOperatorSignature: Signature_normalize(
          permit.riskIdentifications.heavyLiftingChecklist
            .craneOperatorSignature,
          SignatureType.CraneOperator,
        ),
        hasInfraDoneExposuresOnQuay:
          permit.riskIdentifications.heavyLiftingChecklist
            .hasInfraDoneExposuresOnQuay ?? RadioButtonOption.None,
        ifApprovedByInfraName:
          permit.riskIdentifications.heavyLiftingChecklist
            .ifApprovedByInfraName ?? "",
        ifApprovedByInfraNumber:
          permit.riskIdentifications.heavyLiftingChecklist
            .ifApprovedByInfraNumber ?? "",
        isApprovedByInfra:
          permit.riskIdentifications.heavyLiftingChecklist.isApprovedByInfra ??
          RadioButtonOption.None,
        isControlLineAvailable:
          permit.riskIdentifications.heavyLiftingChecklist
            .isControlLineAvailable ?? RadioButtonOption.None,
        isCrainOperatorEducated:
          permit.riskIdentifications.heavyLiftingChecklist
            .isCrainOperatorEducated ?? RadioButtonOption.None,
        isEquipmentTested:
          permit.riskIdentifications.heavyLiftingChecklist.isEquipmentTested ??
          RadioButtonOption.None,
        isGroundGotBearingCapcity:
          permit.riskIdentifications.heavyLiftingChecklist
            .isGroundGotBearingCapcity ?? RadioButtonOption.None,
        isInfraContacted:
          permit.riskIdentifications.heavyLiftingChecklist.isInfraContacted ??
          RadioButtonOption.None,
        isLiftAreaShutOff:
          permit.riskIdentifications.heavyLiftingChecklist.isLiftAreaShutOff ??
          RadioButtonOption.None,
        isLiftingHookOpen:
          permit.riskIdentifications.heavyLiftingChecklist.isLiftingHookOpen ??
          RadioButtonOption.None,
        isLiftLineCorrectForTheWeight:
          permit.riskIdentifications.heavyLiftingChecklist
            .isLiftLineCorrectForTheWeight ?? RadioButtonOption.None,
        isLiftLineInGoodCondition:
          permit.riskIdentifications.heavyLiftingChecklist
            .isLiftLineInGoodCondition ?? RadioButtonOption.None,
        isLiftPlanInPlace:
          permit.riskIdentifications.heavyLiftingChecklist.isLiftPlanInPlace ??
          RadioButtonOption.None,
        isRiskanalysDone:
          permit.riskIdentifications.heavyLiftingChecklist.isRiskanalysDone ??
          RadioButtonOption.None,
        isSignalmanChoosen:
          permit.riskIdentifications.heavyLiftingChecklist.isSignalmanChoosen ??
          RadioButtonOption.None,
        isSupportLegsCorrect:
          permit.riskIdentifications.heavyLiftingChecklist
            .isSupportLegsCorrect ?? RadioButtonOption.None,
        isVehicleAroundRorgatan:
          permit.riskIdentifications.heavyLiftingChecklist
            .isVehicleAroundRorgatan ?? RadioButtonOption.None,
        isWorkDoneBeneathLoad:
          permit.riskIdentifications.heavyLiftingChecklist
            .isWorkDoneBeneathLoad ?? RadioButtonOption.None,
        checklistVersion:
          permit.riskIdentifications.heavyLiftingChecklist.checklistVersion ??
          0,
        isInfrastructureDepartmentContacted:
          permit.riskIdentifications.heavyLiftingChecklist
            .isInfrastructureDepartmentContacted ?? RadioButtonOption.None,
        isLiftAreaShutOff_Cones:
          !!permit.riskIdentifications.heavyLiftingChecklist
            .isLiftAreaShutOff_Cones,
        isLiftAreaShutOff_NA:
          !!permit.riskIdentifications.heavyLiftingChecklist
            .isLiftAreaShutOff_NA,
        isLiftAreaShutOff_Tape:
          !!permit.riskIdentifications.heavyLiftingChecklist
            .isLiftAreaShutOff_Tape,
        isOpenLiftingHookUsed:
          permit.riskIdentifications.heavyLiftingChecklist
            .isOpenLiftingHookUsed ?? RadioButtonOption.None,
      },
      asbetestosChecklist: {
        checklistVersion:
          permit.riskIdentifications.asbetestosChecklist.checklistVersion ?? 0,
        areMasksFittedForRespPerson:
          permit.riskIdentifications.asbetestosChecklist
            .areMasksFittedForRespPerson ?? RadioButtonOption.None,
        areOwnMachinesUsedForSanitation:
          permit.riskIdentifications.asbetestosChecklist
            .areOwnMachinesUsedForSanitation ?? RadioButtonOption.None,
        externalPersonalIsContracted:
          permit.riskIdentifications.asbetestosChecklist
            .externalPersonalIsContracted ?? RadioButtonOption.None,
        hasApprovalFromEnvironmentalAgency:
          permit.riskIdentifications.asbetestosChecklist
            .hasApprovalFromEnvironmentalAgency ?? RadioButtonOption.None,
        hasCorrectDocuments:
          permit.riskIdentifications.asbetestosChecklist.hasCorrectDocuments ??
          RadioButtonOption.None,
        hasSignForAsbestos:
          permit.riskIdentifications.asbetestosChecklist.hasSignForAsbestos ??
          RadioButtonOption.None,
        isSanitisedFromAsbestosDust:
          permit.riskIdentifications.asbetestosChecklist
            .isSanitisedFromAsbestosDust ?? RadioButtonOption.None,
        isTotalTimeForSanitationMoreThanOneHour:
          permit.riskIdentifications.asbetestosChecklist
            .isTotalTimeForSanitationMoreThanOneHour ?? RadioButtonOption.None,
        isWholeMaskWithCompressedAirUsed:
          permit.riskIdentifications.asbetestosChecklist
            .isWholeMaskWithCompressedAirUsed ?? RadioButtonOption.None,
        leaderAndPersonalHaveCorrectEducation:
          permit.riskIdentifications.asbetestosChecklist
            .leaderAndPersonalHaveCorrectEducation ?? RadioButtonOption.None,
        masksAreStoredAccordingAccordingToRules:
          permit.riskIdentifications.asbetestosChecklist
            .masksAreStoredAccordingAccordingToRules ?? RadioButtonOption.None,
        hasApprovalForThisProject:
          permit.riskIdentifications.asbetestosChecklist
            .hasApprovalForThisProject ?? RadioButtonOption.None,
      },
      beefGasChecklist: {
        isContinousGasMeasurementsInPlace:
          permit.riskIdentifications.beefGasChecklist
            .isContinousGasMeasurementsInPlace ?? RadioButtonOption.None,
        isContinuousVentilationInPlace:
          permit.riskIdentifications.beefGasChecklist
            .isContinuousVentilationInPlace ?? RadioButtonOption.None,
        isFreshAirMaskForEveryone:
          permit.riskIdentifications.beefGasChecklist
            .isFreshAirMaskForEveryone ?? RadioButtonOption.None,
        isPortableGasMeasurementAlarmAvailableForEveryone:
          permit.riskIdentifications.beefGasChecklist
            .isPortableGasMeasurementAlarmAvailableForEveryone ??
          RadioButtonOption.None,
        needsFreshAirDevice:
          permit.riskIdentifications.beefGasChecklist.needsFreshAirDevice ??
          RadioButtonOption.None,
        checklistVersion:
          permit.riskIdentifications.beefGasChecklist.checklistVersion ?? 0,
        isContinousGasMeasurementsInPlaceForEntirityOfWork:
          permit.riskIdentifications.beefGasChecklist
            .isContinousGasMeasurementsInPlaceForEntirityOfWork ??
          RadioButtonOption.None,
        isContinuousVentilationNeeded:
          permit.riskIdentifications.beefGasChecklist
            .isContinuousVentilationNeeded ?? RadioButtonOption.None,
        isFreshAirMaskNeededDuringWork:
          permit.riskIdentifications.beefGasChecklist
            .isFreshAirMaskNeededDuringWork ?? RadioButtonOption.None,
        isOxygenLevelGasMeterAvailable:
          permit.riskIdentifications.beefGasChecklist
            .isOxygenLevelGasMeterAvailable ?? RadioButtonOption.None,
        isWindDirectionConsideredDuringWork:
          permit.riskIdentifications.beefGasChecklist
            .isWindDirectionConsideredDuringWork ?? RadioButtonOption.None,
      },
      closedSpaceChecklist: {
        checklistVersion:
          permit.riskIdentifications.closedSpaceChecklist.checklistVersion ?? 0,
        areAllTermsMet:
          permit.riskIdentifications.closedSpaceChecklist.areAllTermsMet ??
          RadioButtonOption.None,
        areGasMeasurementsPerformed:
          permit.riskIdentifications.closedSpaceChecklist
            .areGasMeasurementsPerformed ?? RadioButtonOption.None,
        areGasMeasurementsPerformedInClosedSpace:
          permit.riskIdentifications.closedSpaceChecklist
            .areGasMeasurementsPerformedInClosedSpace ?? RadioButtonOption.None,
        areWarningSignsOnManholes:
          permit.riskIdentifications.closedSpaceChecklist
            .areWarningSignsOnManholes ?? RadioButtonOption.None,
        hasRescueHarness:
          permit.riskIdentifications.closedSpaceChecklist.hasRescueHarness ??
          RadioButtonOption.None,
        isLightningSufficient:
          permit.riskIdentifications.closedSpaceChecklist
            .isLightningSufficient ?? RadioButtonOption.None,
        isManHoleGuardAvailable:
          permit.riskIdentifications.closedSpaceChecklist
            .isManHoleGuardAvailable ?? RadioButtonOption.None,
        isMechanicalVentilation:
          permit.riskIdentifications.closedSpaceChecklist
            .isMechanicalVentilation ?? RadioButtonOption.None,
        isPersonalAtEntryToClosedSpace:
          permit.riskIdentifications.closedSpaceChecklist
            .isPersonalAtEntryToClosedSpace ?? RadioButtonOption.None,
        isThereARescuePlan:
          permit.riskIdentifications.closedSpaceChecklist.isThereARescuePlan ??
          RadioButtonOption.None,
        isThereEmergencyEquipment:
          permit.riskIdentifications.closedSpaceChecklist
            .isThereEmergencyEquipment ?? RadioButtonOption.None,
        willThereBeRescuePlan:
          permit.riskIdentifications.closedSpaceChecklist
            .willThereBeRescuePlan ?? RadioButtonOption.None,
        willYouHaveARescueHarness:
          permit.riskIdentifications.closedSpaceChecklist
            .willYouHaveARescueHarness ?? RadioButtonOption.None,
        willThereBeEmergencyEquipment:
          permit.riskIdentifications.closedSpaceChecklist
            .willThereBeEmergencyEquipment ?? RadioButtonOption.None,
        isTrainedManHoleGuardAvailable:
          permit.riskIdentifications.closedSpaceChecklist
            .isTrainedManHoleGuardAvailable ?? RadioButtonOption.None,
        isOtherPersonnelAtEntry:
          permit.riskIdentifications.closedSpaceChecklist
            .isOtherPersonnelAtEntry ?? RadioButtonOption.None,
        willGasMeasurementsBePerformedAndDocumented:
          permit.riskIdentifications.closedSpaceChecklist
            .willGasMeasurementsBePerformedAndDocumented ??
          RadioButtonOption.None,
        areWarningSignsOnOpenings:
          permit.riskIdentifications.closedSpaceChecklist
            .areWarningSignsOnOpenings ?? RadioButtonOption.None,
      },
      divingChecklist: {
        divingPermitSequenceIdentifier:
          permit.riskIdentifications.divingChecklist
            .divingPermitSequenceIdentifier ?? "",
        fromDivingPermitDocumentId:
          permit.riskIdentifications.divingChecklist.fromDivingPermitDocumentId,
      },
      excavationAndPolingChecklist: {
        canSafleyDecendShaft:
          permit.riskIdentifications.excavationAndPolingChecklist
            .canSafleyDecendShaft ?? RadioButtonOption.None,
        howIsAreaBlockedOff:
          permit.riskIdentifications.excavationAndPolingChecklist
            .howIsAreaBlockedOff ?? "",
        isCablesInTheGround:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isCablesInTheGround ?? RadioButtonOption.None,
        isDepperThan120Centimeters:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isDepperThan120Centimeters ?? RadioButtonOption.None,
        isGasPartOfTheTest:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isGasPartOfTheTest ?? RadioButtonOption.None,
        isManHoleGuardAvailable:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isManHoleGuardAvailable ?? RadioButtonOption.None,
        isMassesOilContanimated:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isMassesOilContanimated ?? RadioButtonOption.None,
        isProductPipesOrTheLikeInTheGround:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isProductPipesOrTheLikeInTheGround ?? RadioButtonOption.None,
        isReleaseDone:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isReleaseDone ?? RadioButtonOption.None,
        isRiskOfRace:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isRiskOfRace ?? RadioButtonOption.None,
        isSubmittedToCountyBoard:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isSubmittedToCountyBoard ?? RadioButtonOption.None,
        isTestsDoneForLab:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isTestsDoneForLab ?? RadioButtonOption.None,
        checklistVersion:
          permit.riskIdentifications.excavationAndPolingChecklist
            .checklistVersion ?? 0,
        isGasTestedInHoleBeforeEntry:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isGasTestedInHoleBeforeEntry ?? RadioButtonOption.None,
        isSubmittedToCountyBoardForExcavation:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isSubmittedToCountyBoardForExcavation ?? RadioButtonOption.None,
        isTrainedExcavationGuardAvailable:
          permit.riskIdentifications.excavationAndPolingChecklist
            .isTrainedExcavationGuardAvailable ?? RadioButtonOption.None,
      },
      hydrogenSulphideChecklist: {
        isContinousGasMeasurementsInPlace:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .isContinousGasMeasurementsInPlace ?? RadioButtonOption.None,
        isContinuousVentilationInPlace:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .isContinuousVentilationInPlace ?? RadioButtonOption.None,
        isEscapeMasksAvailable:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .isEscapeMasksAvailable ?? RadioButtonOption.None,
        isPortalHydroGenSulphideAlarmAvailable:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .isPortalHydroGenSulphideAlarmAvailable ?? RadioButtonOption.None,
        isWindDirectionThoughtOf:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .isWindDirectionThoughtOf ?? RadioButtonOption.None,
        checklistVersion:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .checklistVersion ?? 0,
        isContinousGasMeasurementsInPlaceForEntirityOfWork:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .isContinousGasMeasurementsInPlaceForEntirityOfWork ??
          RadioButtonOption.None,
        isContinuousVentilationNeeded:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .isContinuousVentilationNeeded ?? RadioButtonOption.None,
        isEscapeMasksAvailableForAllInvolved:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .isEscapeMasksAvailableForAllInvolved ?? RadioButtonOption.None,
        isFreshAirMaskNeededDuringWork:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .isFreshAirMaskNeededDuringWork ?? RadioButtonOption.None,
        isHydrogenSulphideGasMeterAvailable:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .isHydrogenSulphideGasMeterAvailable ?? RadioButtonOption.None,
        isWindDirectionConsideredDuringWork:
          permit.riskIdentifications.hydrogenSulphideChecklist
            .isWindDirectionConsideredDuringWork ?? RadioButtonOption.None,
      },
      interventionOnProcessEquipment:
        permit.riskIdentifications.interventionOnProcessEquipment ?? false,
      isAsbestosIncludedInPrint:
        permit.riskIdentifications.isAsbestosIncludedInPrint ?? false,
      isBeefGasIncludedInPrint:
        permit.riskIdentifications.isBeefGasIncludedInPrint ?? false,
      isClosedSpaceIncludedInPrint:
        permit.riskIdentifications.isClosedSpaceIncludedInPrint ?? false,
      isDivingIncludedInPrint:
        permit.riskIdentifications.isDivingIncludedInPrint ?? false,
      isExcavationAndPolingIncludedInPrint:
        permit.riskIdentifications.isExcavationAndPolingIncludedInPrint ??
        false,
      isHeavyLiftingIncludedInPrint:
        permit.riskIdentifications.isHeavyLiftingIncludedInPrint ?? false,
      isHydrogenSulphideIncludedInPrint:
        permit.riskIdentifications.isHydrogenSulphideIncludedInPrint ?? false,
      isRaftIncludedInPrint:
        permit.riskIdentifications.isRaftIncludedInPrint ?? false,
      isScaffoldingIncludedInPrint:
        permit.riskIdentifications.isScaffoldingIncludedInPrint ?? false,
      isWorkAtHeightIncludedInPrint:
        permit.riskIdentifications.isWorkAtHeightIncludedInPrint ?? false,
      raftChecklist: {
        underWaterWorkPermitSequenceIdentifier:
          permit.riskIdentifications.raftChecklist
            .underWaterWorkPermitSequenceIdentifier ?? "",
      },
      scaffoldingChecklist: {
        areEscapeRoutesCleared:
          permit.riskIdentifications.scaffoldingChecklist
            .areEscapeRoutesCleared ?? RadioButtonOption.None,
        doesScaffoldHaveDamage:
          permit.riskIdentifications.scaffoldingChecklist
            .doesScaffoldHaveDamage ?? RadioButtonOption.None,
        doesScaffoldHaveSufficientRailing:
          permit.riskIdentifications.scaffoldingChecklist
            .doesScaffoldHaveSufficientRailing ?? RadioButtonOption.None,
        ifPassageAffectedIsMarked:
          permit.riskIdentifications.scaffoldingChecklist
            .ifPassageAffectedIsMarked ?? RadioButtonOption.None,
        isConnectedProperly:
          permit.riskIdentifications.scaffoldingChecklist.isConnectedProperly ??
          RadioButtonOption.None,
        isHarnessInspected:
          permit.riskIdentifications.scaffoldingChecklist.isHarnessInspected ??
          RadioButtonOption.None,
        isHarnessNeeded:
          permit.riskIdentifications.scaffoldingChecklist.isHarnessNeeded ??
          RadioButtonOption.None,
        isIceOnScaffold:
          permit.riskIdentifications.scaffoldingChecklist.isIceOnScaffold ??
          RadioButtonOption.None,
        isLadderHigherThan4MetersAndBackProtection:
          permit.riskIdentifications.scaffoldingChecklist
            .isLadderHigherThan4MetersAndBackProtection ??
          RadioButtonOption.None,
        isOilSpillOnScaffold:
          permit.riskIdentifications.scaffoldingChecklist
            .isOilSpillOnScaffold ?? RadioButtonOption.None,
        isPassageAffectedByScaffold:
          permit.riskIdentifications.scaffoldingChecklist
            .isPassageAffectedByScaffold ?? RadioButtonOption.None,
        isScaffoldApproved:
          permit.riskIdentifications.scaffoldingChecklist.isScaffoldApproved ??
          RadioButtonOption.None,
        isThereRiskOfStepThrough:
          permit.riskIdentifications.scaffoldingChecklist
            .isThereRiskOfStepThrough ?? RadioButtonOption.None,
        noLoosePlanksOnScaffold:
          permit.riskIdentifications.scaffoldingChecklist
            .noLoosePlanksOnScaffold ?? RadioButtonOption.None,
        willScaffoldsBeBuiltInPumpingStation:
          permit.riskIdentifications.scaffoldingChecklist
            .willScaffoldsBeBuiltInPumpingStation ?? RadioButtonOption.None,
      },
      scaffoldingConstruction:
        permit.riskIdentifications.scaffoldingConstruction ?? false,
      scaffoldingConstructionChecklist: {
        checklistVersion:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .checklistVersion ?? 0,
        willBeBuiltInPumpingStation:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .willBeBuiltInPumpingStation ?? RadioButtonOption.None,
        willBeBuiltOnQuay:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .willBeBuiltOnQuay ?? RadioButtonOption.None,
        isFallProtectionHarnessNeeded:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .isFallProtectionHarnessNeeded ?? RadioButtonOption.None,
        isHarnessInspectedWithinOneYear:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .isHarnessInspectedWithinOneYear ?? RadioButtonOption.None,
        isSecurelyConnectedToFallProtection:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .isSecurelyConnectedToFallProtection ?? RadioButtonOption.None,
        willHaveApprovedValidityCertificate:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .willHaveApprovedValidityCertificate ?? RadioButtonOption.None,
        willHaveSufficientlyHighGuardRails:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .willHaveSufficientlyHighGuardRails ?? RadioButtonOption.None,
        willToeboardsBeMounted:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .willToeboardsBeMounted ?? RadioButtonOption.None,
        willHaveLoosePlanks:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .willHaveLoosePlanks ?? RadioButtonOption.None,
        isRiskOfSteppingThroughAfterCompletion:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .isRiskOfSteppingThroughAfterCompletion ?? RadioButtonOption.None,
        isBackProtectionMountedIfLadderAbove4m:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .isBackProtectionMountedIfLadderAbove4m ?? RadioButtonOption.None,
        areEscapeRoutesCleared:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .areEscapeRoutesCleared ?? RadioButtonOption.None,
        hasScaffoldingDamage:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .hasScaffoldingDamage ?? RadioButtonOption.None,
        hasOilSpill:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .hasOilSpill ?? RadioButtonOption.None,
        hasIce:
          permit.riskIdentifications.scaffoldingConstructionChecklist.hasIce ??
          RadioButtonOption.None,
        isPassageAffectedByScaffolding:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .isPassageAffectedByScaffolding ?? RadioButtonOption.None,
        isPassageMarkedAndCollisionProtectionInstalled:
          permit.riskIdentifications.scaffoldingConstructionChecklist
            .isPassageMarkedAndCollisionProtectionInstalled ??
          RadioButtonOption.None,
      },
      scaffoldingWork: permit.riskIdentifications.scaffoldingWork ?? false,
      scaffoldingWorkChecklist: {
        checklistVersion:
          permit.riskIdentifications.scaffoldingWorkChecklist
            .checklistVersion ?? 0,
        hasApprovedValidityCertificate:
          permit.riskIdentifications.scaffoldingWorkChecklist
            .hasApprovedValidityCertificate ?? RadioButtonOption.None,
        hasSufficientlyHighGuardRails:
          permit.riskIdentifications.scaffoldingWorkChecklist
            .hasSufficientlyHighGuardRails ?? RadioButtonOption.None,
        hasToeboards:
          permit.riskIdentifications.scaffoldingWorkChecklist.hasToeboards ??
          RadioButtonOption.None,
        hasLoosePlanks:
          permit.riskIdentifications.scaffoldingWorkChecklist.hasLoosePlanks ??
          RadioButtonOption.None,
        isRiskOfSteppingThrough:
          permit.riskIdentifications.scaffoldingWorkChecklist
            .isRiskOfSteppingThrough ?? RadioButtonOption.None,
        isBackProtectionMountedIfLadderAbove4m:
          permit.riskIdentifications.scaffoldingWorkChecklist
            .isBackProtectionMountedIfLadderAbove4m ?? RadioButtonOption.None,
        areEscapeRoutesCleared:
          permit.riskIdentifications.scaffoldingWorkChecklist
            .areEscapeRoutesCleared ?? RadioButtonOption.None,
        hasScaffoldingDamage:
          permit.riskIdentifications.scaffoldingWorkChecklist
            .hasScaffoldingDamage ?? RadioButtonOption.None,
        hasOilSpill:
          permit.riskIdentifications.scaffoldingWorkChecklist.hasOilSpill ??
          RadioButtonOption.None,
        hasIce:
          permit.riskIdentifications.scaffoldingWorkChecklist.hasIce ??
          RadioButtonOption.None,
      },
      workAtHeightChecklist: {
        areEscapeRoutesCleared:
          permit.riskIdentifications.workAtHeightChecklist
            .areEscapeRoutesCleared ?? RadioButtonOption.None,
        haveCorrectFallProtection:
          permit.riskIdentifications.workAtHeightChecklist
            .haveCorrectFallProtection ?? RadioButtonOption.None,
        isAreaCordonedOff:
          permit.riskIdentifications.workAtHeightChecklist.isAreaCordonedOff ??
          RadioButtonOption.None,
        isConnectedProperly:
          permit.riskIdentifications.workAtHeightChecklist
            .isConnectedProperly ?? RadioButtonOption.None,
        isHarnessInspected:
          permit.riskIdentifications.workAtHeightChecklist.isHarnessInspected ??
          RadioButtonOption.None,
        isRiskOfDebris:
          permit.riskIdentifications.workAtHeightChecklist.isRiskOfDebris ??
          RadioButtonOption.None,
        isRiskOfFallingObjects:
          permit.riskIdentifications.workAtHeightChecklist
            .isRiskOfFallingObjects ?? RadioButtonOption.None,
        needsProtectiveNet:
          permit.riskIdentifications.workAtHeightChecklist.needsProtectiveNet ??
          RadioButtonOption.None,
        willBeLooseObjects:
          permit.riskIdentifications.workAtHeightChecklist.willBeLooseObjects ??
          RadioButtonOption.None,
        willBeLooseObjectsComment:
          permit.riskIdentifications.workAtHeightChecklist
            .willBeLooseObjectsComment ?? "",
        checklistVersion:
          permit.riskIdentifications.workAtHeightChecklist.checklistVersion ??
          0,
        howIsAreaBelowCordonedOff:
          permit.riskIdentifications.workAtHeightChecklist
            .howIsAreaBelowCordonedOff ?? "",
        isSecurelyConnectedToFallProtection:
          permit.riskIdentifications.workAtHeightChecklist
            .isSecurelyConnectedToFallProtection ?? RadioButtonOption.None,
        isHarnessInspectedWithinOneYear:
          permit.riskIdentifications.workAtHeightChecklist
            .isHarnessInspectedWithinOneYear ?? RadioButtonOption.None,
        howAreLooseObjectsSecured:
          permit.riskIdentifications.workAtHeightChecklist
            .howAreLooseObjectsSecured ?? "",
      },
    },
    safeHarbourEducationControlledSignature: Signature_normalize(
      permit.safeHarbourEducationControlledSignature,
      SignatureType.SafeHarbourEducationControlled,
    ),
    isFinishedPermitSeekerSignature: Signature_normalize(
      permit.isFinishedPermitSeekerSignature,
      SignatureType.IsFinishedPermitSeeker,
    ),
    isFinishedPermitProviderSignature: Signature_normalize(
      permit.isFinishedPermitProviderSignature,
      SignatureType.IsFinishedPermitProvider,
    ),
    initialStartDate: new Date(permit.initialStartDate),
    extendedEndDate: new Date(permit.extendedEndDate),
    measures: Measures_normalize(permit.measures),
    alertInfo: permit.alertInfo ? permit.alertInfo : [],
    createdByUserName: permit.createdByUserName ?? "",
    id: permit.id ?? "",
    allInvolvedHaveSafeHarbourEducation:
      permit.allInvolvedHaveSafeHarbourEducation ?? false,
    canAttestPermitDone: permit.canAttestPermitDone ?? false,
    canAttestPermitTidyed: permit.canAttestPermitTidyed ?? false,
    departmentId: permit.departmentId ?? 0,
    entityId: permit.entityId ?? 0,
    entrepreneur: permit.entrepreneur ?? "",
    description: permit.description ?? "",
    permitName: permit.permitName ?? PermitType.EnergyHarbourPermit,
    status: permit.status ?? CodeStatus.Registered,
    sequenceIdentifier: permit.sequenceIdentifier ?? "",
    version: permit.version ?? 0,
    hasAttachedFile: permit.hasAttachedFile ?? false,
    isFinished: permit.isFinished ?? false,
    remindMe: permit.remindMe ?? false,
    expirationReminderEmail: permit.expirationReminderEmail ?? undefined,
    expirationReminderPhoneNumber:
      permit.expirationReminderPhoneNumber ?? undefined,
    orderedBy: permit.orderedBy,
    orderedByCompanyId: permit.orderedByCompanyId,
    orderedByCompanyDepartmentId: permit.orderedByCompanyDepartmentId,
    orderedByCustomerNameSnapshot: permit.orderedByCustomerNameSnapshot,
    workLeader: permit.workLeader ?? Person_default(),
    educationCheckPerformed: permit.educationCheckPerformed ?? false,
    projectLeader: permit.projectLeader ?? Person_default(),
    harbourOffice: permit.harbourOffice ?? HarbourOffice.Skarvik,
    hotWorkCategoryOneOptions: {
      angleGrinder: permit.hotWorkCategoryOneOptions.angleGrinder ?? false,
      cuttingTorch: permit.hotWorkCategoryOneOptions.cuttingTorch ?? false,
      gasBurner: permit.hotWorkCategoryOneOptions.gasBurner ?? false,
      hotAirGun: permit.hotWorkCategoryOneOptions.hotAirGun ?? false,
      openFlameSoldering:
        permit.hotWorkCategoryOneOptions.openFlameSoldering ?? false,
      other: permit.hotWorkCategoryOneOptions.other ?? "",
      plasmaCutter: permit.hotWorkCategoryOneOptions.plasmaCutter ?? false,
      welding: permit.hotWorkCategoryOneOptions.welding ?? false,
    },
    hotWorkCategoryTwoOptions: {
      exCamera: permit.hotWorkCategoryTwoOptions.exCamera ?? false,
      excavator: permit.hotWorkCategoryTwoOptions.excavator ?? false,
      GPS:
        permit.hotWorkCategoryTwoOptions.GPS ??
        (permit.hotWorkCategoryTwoOptions as any).gps ??
        false,
      megger: permit.hotWorkCategoryTwoOptions.megger ?? false,
      multimeter: permit.hotWorkCategoryTwoOptions.multimeter ?? false,
      openEquipment: permit.hotWorkCategoryTwoOptions.openEquipment ?? false,
      other: permit.hotWorkCategoryTwoOptions.other ?? "",
      screwDriverDrill:
        permit.hotWorkCategoryTwoOptions.screwDriverDrill ?? false,
      totalStation: permit.hotWorkCategoryTwoOptions.totalStation ?? false,
      circularSaw: permit.hotWorkCategoryTwoOptions.circularSaw ?? false,
      fiberWelding: permit.hotWorkCategoryTwoOptions.fiberWelding ?? false,
      sparkFreeGrindingWheel:
        permit.hotWorkCategoryTwoOptions.sparkFreeGrindingWheel ?? false,
      tigersaw: permit.hotWorkCategoryTwoOptions.tigersaw ?? false,
      version:
        permit.hotWorkCategoryTwoOptions.version ??
        HOT_WORK_CATEGORY_TWO_OPTIONS_VERSION_2026_03,
    },
    hotWorkCategoryThreeOptions: {
      boat: permit.hotWorkCategoryThreeOptions.boat ?? false,
      craneTruck: permit.hotWorkCategoryThreeOptions.craneTruck ?? false,
      drone: permit.hotWorkCategoryThreeOptions.drone ?? false,
      excavator: permit.hotWorkCategoryThreeOptions.excavator ?? false,
      generator: permit.hotWorkCategoryThreeOptions.generator ?? false,
      GPS:
        permit.hotWorkCategoryThreeOptions.GPS ??
        (permit.hotWorkCategoryThreeOptions as any).gps ??
        false,
      other: permit.hotWorkCategoryThreeOptions.other ?? "",
      multimeter: permit.hotWorkCategoryThreeOptions.multimeter ?? false,
      nonExCamera: permit.hotWorkCategoryThreeOptions.nonExCamera ?? false,
      openExClassifiedEquipment:
        permit.hotWorkCategoryThreeOptions.openExClassifiedEquipment ?? false,
      vacuumTruck: permit.hotWorkCategoryThreeOptions.vacuumTruck ?? false,
    },
    lastPermitInSeriesDocumentId: permit.lastPermitInSeriesDocumentId ?? "",
    renewalOfPermitSequenceIdentifier:
      permit.renewalOfPermitSequenceIdentifier ?? "",
    permitCategories: permit.permitCategories ?? [],
    permitProviderSignatureDate:
      permit.permitProviderSignatureDate ?? new Date(),
    PermitSeekerSignatureDate: permit.PermitSeekerSignatureDate ?? new Date(),
    persons: permit.persons ?? [],
    protectiveEquipment: {
      gasWarner: {
        forH2S: permit.protectiveEquipment.gasWarner.forH2S ?? false,
        forO2: permit.protectiveEquipment.gasWarner.forO2 ?? false,
        forLEL: permit.protectiveEquipment.gasWarner.forLEL ?? false,
        personalGasWarner:
          permit.protectiveEquipment.gasWarner.personalGasWarner ?? false,
      },
      other: permit.protectiveEquipment.other ?? "",
      usingFreshAirMask: permit.protectiveEquipment.usingFreshAirMask ?? false,
      usingGasMask: permit.protectiveEquipment.usingGasMask ?? false,
      usingHelmet: permit.protectiveEquipment.usingHelmet ?? false,
      usingLifeJacket: permit.protectiveEquipment.usingLifeJacket ?? false,
      usingProtectiveGlasses:
        permit.protectiveEquipment.usingProtectiveGlasses ?? false,
    },
    showMissingHotWorkSignatureAlert:
      permit.showMissingHotWorkSignatureAlert ?? false,
    renewedByPermitSequenceIdentifier:
      permit.renewedByPermitSequenceIdentifier ?? "",
    contentVersion:
      permit.contentVersion ??
      Math.max(...Object.values(permitContentVersions)),

    canAccept: permit.canAccept ?? false,
    canCheckout: permit.canCheckout ?? false,
    canPrint: permit.canPrint ?? false,
    canCopy: permit.canCopy ?? false,
    canManageAttachments: permit.canManageAttachments ?? false,
    canManageOrderedBy: permit.canManageOrderedBy ?? false,
    canUpdateParticipants: permit.canUpdateParticipants ?? false,
    canUpdatePositionActiveStatus:
      permit.canUpdatePositionActiveStatus ?? false,
    canViewFinishPermitLink: permit.canViewFinishPermitLink ?? false,
    canUpdate: permit.canUpdate ?? false,
    canRenew: permit.canRenew ?? false,
    canManageHotWorkConditions: permit.canManageHotWorkConditions ?? false,
    canUpdatePermitAgreementEndDate:
      permit.canUpdatePermitAgreementEndDate ?? false,
    canUpdateDescription: permit.canUpdateDescription ?? false,
    canUpdateStartDate: permit.canUpdateStartDate ?? false,
    canUpdateEndDate: permit.canUpdateEndDate ?? false,
    canUpdatePositions: permit.canUpdatePositions ?? false,
    isColdWorkCategoryRequired: permit.isColdWorkCategoryRequired ?? false,
    canManagePositionPolygons: permit.canManagePositionPolygons ?? false,
    canManagePositionCategories: permit.canManagePositionCategories ?? false,
    canManagePermitCategory: permit.canManagePermitCategory ?? false,
  };

  return normalizedPermit;
}

export function EnergyHarbourPermit_toFormState(
  permit: EnergyHarbourPermit,
): EnergyHarbourPermitFormState {
  const formState: EnergyHarbourPermitFormState = {
    allInvolvedHaveSafeHarbourEducation: formElement(
      permit.allInvolvedHaveSafeHarbourEducation,
    ),
    canAttestPermitDone: formElement(permit.canAttestPermitDone),
    canAttestPermitTidyed: formElement(permit.canAttestPermitTidyed),
    description: formElement(permit.description),
    educationCheckPerformed: formElement(permit.educationCheckPerformed),
    endDate: formElement(permit.endDate),
    entrepreneur: formElement(permit.entrepreneur),
    expirationReminderEmail: formElement(permit.expirationReminderEmail),
    expirationReminderPhoneNumber: formElement(
      permit.expirationReminderPhoneNumber,
    ),
    harbourOffice: formElement(permit.harbourOffice),
    hotWork: HotWork_toFormState(permit.hotWork),
    hotWorkCategoryOneConditions: HotWork_toFormState(
      permit.hotWorkCategoryOneConditions,
    ),
    hotWorkCategoryTwoConditions: HotWork_toFormState(
      permit.hotWorkCategoryTwoConditions,
    ),
    hotWorkCategoryThreeConditions: HotWork_toFormState(
      permit.hotWorkCategoryThreeConditions,
    ),
    hotWorkCategoryOneOptions: HotWorkCategoryOneOptions_toFormState(
      permit.hotWorkCategoryOneOptions,
    ),
    hotWorkCategoryTwoOptions: HotWorkCategoryTwoOptions_toFormState(
      permit.hotWorkCategoryTwoOptions,
    ),
    hotWorkCategoryThreeOptions: HotWorkCategoryThreeOptions_toFormState(
      permit.hotWorkCategoryThreeOptions,
    ),
    measures: Measures_toFormState(permit.measures),
    orderedBy: formElement(permit.orderedBy),
    permitAgreement: PermitAgreement_toFormState(permit.permitAgreement),
    persons: permit.persons.map((p, i) => ({
      index: i,
      isActive: p.isActive,
      name: p.name,
      phoneNumber: p.phoneNumber,
    })),
    positions: permit.positions.map(Position_toPositionPickerRow),
    projectLeader: Person_toFormElement(permit.projectLeader),
    protectiveEquipment: ProtectiveEquipment_toFormState(
      permit.protectiveEquipment,
    ),
    remindMe: formElement(permit.remindMe),
    remindMeDate: formElement(permit.remindMeDate),
    riskIdentifications: RiskIdentifications_toFormState(
      permit.riskIdentifications,
    ),
    startDate: formElement(permit.startDate),
    workLeader: Person_toFormElement(permit.workLeader),
    permitCategories: formElement(permit.permitCategories),
    orderedByCustomer: formElement({
      companyDepartmentId: permit.orderedByCompanyDepartmentId,
      companyId: permit.orderedByCompanyId,
    }),
  };

  return formState;
}
